import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';

export interface Book {
  id: string;
  title: string;
  genre: string;
  premise: string;
  status: string;
  toneStyle: string;
  targetAudience: string;
  contentRating: string;
  targetWordCount: number;
  chaptersTotal: number;
  chaptersComplete: number;
  wordCount: number;
  createdAt: unknown;
  updatedAt: unknown;
  errorMessage?: string | null;
  coverSelectedId?: string | null;
}

interface CreateBookInput {
  title: string;
  genre: string;
  premise: string;
  targetWordCount: 30000 | 50000 | 80000;
  toneStyle: string;
  targetAudience: string;
  contentRating: string;
  sequelFlag?: boolean;
}

/** Statuses that indicate active generation — poll every 3 s so the UI updates automatically. */
const IN_PROGRESS_STATUSES = new Set(['setup', 'outline', 'characters', 'writing']);

export function useBooks(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['books'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ books: Book[] }>>('/books');
      return res.data.data?.books ?? [];
    },
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      const books = query.state.data;
      if (books?.some((b) => IN_PROGRESS_STATUSES.has(b.status))) return 3000;
      return false;
    },
  });
}

export function useBook(bookId: string | undefined) {
  return useQuery({
    queryKey: ['books', bookId],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<Book>>(`/books/${bookId}`);
      return res.data.data ?? null;
    },
    enabled: !!bookId,
    // Poll every 3 s while the book is being generated so status transitions surface immediately.
    refetchInterval: (query) => {
      const book = query.state.data;
      if (book && IN_PROGRESS_STATUSES.has(book.status)) {
        return 3000;
      }
      return false;
    },
  });
}

export function useCreateBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBookInput) => {
      const res = await api.post<ApiSuccess<{ bookId: string; status: string; creditsDeducted: number; creditsRemaining: number }>>('/books', input);
      return res.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['books'] });
      void queryClient.invalidateQueries({ queryKey: ['credits'] });
    },
  });
}

export function useResumeBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bookId: string) => {
      const res = await api.post<ApiSuccess<{ bookId: string; resumedFromChapter: number }>>(
        `/books/${bookId}/resume`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['books'] });
    },
  });
}

export function useDeleteBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bookId: string) => {
      const res = await api.delete<ApiSuccess<{ deleted: boolean }>>(`/books/${bookId}`);
      return res.data.data;
    },
    onMutate: async (bookId: string) => {
      await queryClient.cancelQueries({ queryKey: ['books'] });
      const previous = queryClient.getQueryData<Book[]>(['books']);
      queryClient.setQueryData<Book[]>(['books'], (old) => (old ?? []).filter((b) => b.id !== bookId));
      return { previous };
    },
    onError: (_err, _bookId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['books'], context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['books'] });
    },
  });
}
